/**
 * 客户 H5 资料下载编排器（Issue #391 P1 返修）。
 *
 * 把签名授权 → 签名 URL fetch → content-type / response.ok 校验 →
 * Blob 触发下载 → object URL 回收抽成纯函数，便于 vitest 注入依赖断言，
 * 不需要真实 fetch、DOM 或 OSS。
 *
 * 红线：
 *   - 必须真实 fetch 签名 URL，不允许直接 window.open / window.location 跳转。
 *   - response.ok 为 false 时必须返回包含 HTTP status 的明确错误。
 *   - content-type 缺失、与文档 mimeType 不兼容、命中 forbidden allowlist
 *     必须受控失败；不得回退到原始签名 URL。
 *   - 不持久化签名 URL；使用 Blob/object URL 后必须及时 revokeObjectURL。
 *   - 不修改 /api/v1/me/* 身份契约：仍仅依赖后端解析 JWT。
 */
import type { ApiComponents } from '@jinzhuan/api-client';
import { meError, type MeFetchError } from './classify';

export type SignedDownloadGrantDto =
  ApiComponents['schemas']['SignedDownloadGrantDto'];

export interface FetchSignedDocumentResponse {
  readonly ok: boolean;
  readonly status: number;
  readonly contentType: string | null;
  readonly blob: () => Promise<Blob>;
}

export type FetchSignedDocumentFn = (
  url: string,
) => Promise<FetchSignedDocumentResponse>;

export interface DownloadOrchestratorDeps {
  fetchDownloadGrant: (
    documentId: string,
  ) => Promise<SignedDownloadGrantDto | MeFetchError>;
  fetchSignedDocument: FetchSignedDocumentFn;
  createObjectUrl: (blob: Blob) => string;
  revokeObjectUrl: (url: string) => void;
  triggerObjectUrlDownload: (objectUrl: string, filename: string) => void;
}

export interface DownloadFlowInput {
  documentId: string;
  expectedMimeType: string;
  filename: string;
}

export interface DownloadFlowSuccess {
  kind: 'ok';
}

export type DownloadFlowResult = DownloadFlowSuccess | MeFetchError;

/**
 * 允许的客户资料下载 MIME 列表。
 *
 * 之所以维护一份 narrow allowlist：
 *   1. 当 OSS / 代理返回 application/octet-stream 但 doc.mimeType 是具体类型时，
 *      我们仍允许下载，但要先确认它不是 text/html 之类的危险类型。
 *   2. 当 doc.mimeType 缺失或为通用值时，至少要把响应限制在已知安全类型。
 */
const DOWNLOAD_ALLOWED_MEDIA_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'application/octet-stream',
  'application/zip',
  'application/x-zip-compressed',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/svg+xml',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/mp4',
  'text/plain',
]);

/**
 * 显式禁止的响应媒体类型。
 *
 * `text/html` 是 OSS 鉴权失败常见的 fallback 响应，必须拦截，避免
 * 把伪装成签名失败页的 HTML 当作客户资料下载给客户端。
 */
const DOWNLOAD_FORBIDDEN_MEDIA_TYPES: ReadonlySet<string> = new Set([
  'text/html',
  'application/xhtml+xml',
  'application/javascript',
  'text/javascript',
]);

export function parseMediaType(
  contentType: string | null | undefined,
): string | null {
  if (!contentType) return null;
  const head = contentType.split(';', 1)[0];
  if (!head) return null;
  const trimmed = head.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

export function isAcceptableDownloadContentType(
  responseContentType: string | null | undefined,
  documentMimeType: string | null | undefined,
): boolean {
  const actual = parseMediaType(responseContentType);
  if (!actual) return false;
  if (DOWNLOAD_FORBIDDEN_MEDIA_TYPES.has(actual)) return false;

  const expected = parseMediaType(documentMimeType);

  // 与文档登记 mimeType 精确一致是首选路径。
  if (expected && actual === expected) return true;

  // 文档登记为通用 application/octet-stream 时，回退到 narrow allowlist。
  if (
    (!expected || expected === 'application/octet-stream') &&
    DOWNLOAD_ALLOWED_MEDIA_TYPES.has(actual)
  ) {
    return true;
  }

  // OSS 偶尔会把任何二进制资源标记为 application/octet-stream，
  // 文档登记类型在 allowlist 中时允许该 fallback。
  if (
    actual === 'application/octet-stream' &&
    expected &&
    DOWNLOAD_ALLOWED_MEDIA_TYPES.has(expected)
  ) {
    return true;
  }

  return false;
}

export function isMeFetchErrorResult(value: unknown): value is MeFetchError {
  if (!value || typeof value !== 'object') return false;
  const obj = value as { kind?: unknown; message?: unknown };
  return (
    typeof obj.kind === 'string' &&
    typeof obj.message === 'string' &&
    [
      'session_expired',
      'forbidden',
      'not_found',
      'conflict',
      'network',
      'unknown',
    ].includes(obj.kind)
  );
}

export async function executeDownloadFlow(
  deps: DownloadOrchestratorDeps,
  input: DownloadFlowInput,
): Promise<DownloadFlowResult> {
  const grant = await deps.fetchDownloadGrant(input.documentId);
  if (isMeFetchErrorResult(grant)) return grant;
  if (!grant.downloadUrl) {
    return meError('unknown', '下载授权缺少签名 URL，已中止下载');
  }

  let response: FetchSignedDocumentResponse;
  try {
    response = await deps.fetchSignedDocument(grant.downloadUrl);
  } catch {
    return meError('network', '下载文件失败，请检查网络后重试');
  }

  if (!response.ok) {
    return meError(
      'unknown',
      `下载失败（HTTP ${response.status}），请稍后重试或联系顾问`,
    );
  }

  const rawContentType = response.contentType;
  if (rawContentType === null || rawContentType === undefined) {
    return meError(
      'unknown',
      '下载响应缺少 content-type，已中止下载以保护安全',
    );
  }
  if (!isAcceptableDownloadContentType(rawContentType, input.expectedMimeType)) {
    const parsed = parseMediaType(rawContentType) ?? '未知';
    return meError(
      'unknown',
      `下载响应类型异常（${parsed}），已中止下载以保护安全`,
    );
  }

  let blob: Blob;
  try {
    blob = await response.blob();
  } catch {
    return meError('network', '下载文件解析失败，请稍后重试');
  }

  const objectUrl = deps.createObjectUrl(blob);
  try {
    deps.triggerObjectUrlDownload(objectUrl, input.filename);
  } finally {
    deps.revokeObjectUrl(objectUrl);
  }

  return { kind: 'ok' };
}

export async function defaultFetchSignedDocument(
  url: string,
): Promise<FetchSignedDocumentResponse> {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    redirect: 'follow',
  });
  return {
    ok: res.ok,
    status: res.status,
    contentType: res.headers.get('content-type'),
    blob: () => res.blob(),
  };
}

export function defaultCreateObjectUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

export function defaultRevokeObjectUrl(url: string): void {
  URL.revokeObjectURL(url);
}

export function defaultTriggerObjectUrlDownload(
  objectUrl: string,
  filename: string,
): void {
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
  }
}
